import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Download } from "lucide-react";
import { API } from "../context/AppContext";
import ImageOrArt from "../components/ImageOrArt";
import GeoArt from "../components/GeoArt";
import { MaskedLines, Reveal, SectionHead } from "../components/Reveal";

const CATEGORIES = ["All", "Press", "Exhibitions", "Studio Journal", "Curatorial Notes", "DATOS Updates"];

export default function News() {
  const [items, setItems] = useState([]);
  const [kit, setKit] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    axios.get(`${API}/news`, { params: cat === "All" ? {} : { category: cat } }).then(({ data }) => setItems(data)).catch(() => {});
  }, [cat]);

  useEffect(() => {
    axios.get(`${API}/mediakit`).then(({ data }) => setKit(data)).catch(() => {});
  }, []);

  const featured = items.find((n) => n.featured);

  return (
    <main className="pt-24 sm:pt-32" data-testid="news-page">
      <section className="container-x section-y">
        <p className="eyebrow mb-6">Press & Journal</p>
        <MaskedLines lines={["News & Media"]} className="h-display" />
      </section>

      {featured && cat === "All" && (
        <section className="border-t border-border" data-testid="voyage-feature">
          <div className="container-x section-y grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div className="border-2 border-[#B8860B] p-4 rotate-1 hover:rotate-0 transition-transform duration-700">
                <ImageOrArt image={featured.image} seed={featured.seed} ratio="16/10" className="w-full" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                <p className="font-mono-x text-xs text-[#B8860B] uppercase tracking-[0.25em] mb-4">Featured — {featured.source}</p>
                <h2 className="h-section mb-6" data-testid="voyage-title">{featured.title}</h2>
                <p className="text-muted-foreground max-w-md mb-8">{featured.excerpt} {featured.body}</p>
                <span className="btn-ghost" data-testid="voyage-read">Read the Interview <ArrowUpRight size={16} /></span>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="border-t border-border">
        <div className="container-x section-y">
          <div className="flex flex-wrap gap-2 mb-14" data-testid="news-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`news-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 border transition-colors duration-300 ${cat === c ? "bg-[#FF1A1A] border-[#FF1A1A] text-white" : "border-border hover:border-[#FF1A1A] hover:text-[#FF1A1A]"}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="space-y-px bg-border border border-border" data-testid="news-list">
            {items.map((n) => (
              <article key={n.id} className="bg-card p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-12 gap-4 items-baseline group" data-testid={`news-item-${n.id}`}>
                <p className="font-mono-x text-xs text-[#FF1A1A] sm:col-span-2">{n.date}</p>
                <div className="sm:col-span-8">
                  <p className="font-mono-x text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{n.category} — {n.source}</p>
                  <h3 className="font-display font-extrabold uppercase text-lg group-hover:text-[#FF1A1A] transition-colors">{n.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{n.excerpt}</p>
                </div>
                <ArrowUpRight size={18} className="sm:col-span-2 justify-self-end text-muted-foreground group-hover:text-[#FF1A1A] transition-colors" />
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="container-x section-y">
          <SectionHead eyebrow="Media Kit" title="Downloads for Press & Partners" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="mediakit-list">
            {kit.map((d, i) => (
              <Reveal key={d.slug} delay={i * 0.05}>
                <a
                  href={`${API}/mediakit/${d.slug}`}
                  download
                  data-testid={`mediakit-${d.slug}`}
                  className="card-line p-6 flex flex-col justify-between min-h-[160px] group hover:border-[#FF1A1A] transition-colors duration-300"
                >
                  <Download size={18} className="text-muted-foreground group-hover:text-[#FF1A1A] transition-colors" />
                  <div>
                    <p className="font-display font-bold uppercase text-sm mt-6">{d.title}</p>
                    <p className="font-mono-x text-[10px] text-muted-foreground mt-1">PDF</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
